import React, { useState, useEffect } from 'react';
import { StyleSheet, View, TouchableOpacity, Image, Platform, Linking } from 'react-native';
import { ODSText } from '../../atoms/ODSText';
import ODSButton from '../../atoms/ODSButton';
import { ODSInput } from '../../atoms/ODSInput';
import ODSDatepicker from '../../atoms/ODSDatepicker';
import { getTokens } from '../../../design-tokens/color-tokens';
import getScreenType from '../../../utils/getScreenType';
import { ODSIcon } from '../../atoms/ODSIcon';
import moment from 'moment';

const layout = getScreenType();

interface MeterState {
    date: Date | null;
    value: string;
    supplier: string;
}

interface MetersMap {
    electric: MeterState;
    gas: MeterState;
    water: MeterState;
    oil: MeterState;
}

interface MeterErrors {
    date?: string;
    value?: string;
    supplier?: string;
}

interface ErrorsMap {
    electric: MeterErrors;
    gas: MeterErrors;
    water: MeterErrors;
    oil: MeterErrors;
}

interface CheckOutBoxProps {
    restStyle?: any;
    data?: any;
    onSaveMeterReadings?: (payload: any, callback: () => void) => void;
    customErrors?: ErrorsMap;
}

const CheckOutBox = ({ restStyle, data, onSaveMeterReadings, customErrors }: CheckOutBoxProps) => {
    const themeObj = getTokens('light').colors;
    const [isEditing, setIsEditing] = useState(false);

    const [meters, setMeters] = useState<MetersMap>({
        electric: {
            date: null,
            value: '',
            supplier: '',
        },
        gas: {
            date: null,
            value: '',
            supplier: '',
        },
        water: {
            date: null,
            value: '',
            supplier: '',
        },
        oil: {
            date: null,
            value: '',
            supplier: '',
        },
    });

    const [errors, setErrors] = useState<ErrorsMap>({
        electric: {},
        gas: {},
        water: {},
        oil: {},
    });

    useEffect(() => {
        const resolvedData = data?.data || data;
        if (resolvedData?.open_flow_move_out_meter_readings && Array.isArray(resolvedData.open_flow_move_out_meter_readings)) {
            const updated = {
                electric: { date: null, value: '', supplier: '' },
                gas: { date: null, value: '', supplier: '' },
                water: { date: null, value: '', supplier: '' },
                oil: { date: null, value: '', supplier: '' },
            };
            resolvedData.open_flow_move_out_meter_readings.forEach((item: any) => {
                const type = item?.utility_type?.toLowerCase() as keyof MetersMap;
                if (updated[type]) {
                    updated[type] = {
                        date: item?.reading_date ? new Date(item.reading_date) : null,
                        value: item?.reading_value?.toString() || '',
                        supplier: item?.supplier || '',
                    };
                }
            });
            setMeters(updated);
        }
    }, [data]);

    const updateMeterField = (meterType: keyof MetersMap, field: keyof MeterState, val: any) => {
        let cleanVal = val;
        if (field === 'value' && typeof cleanVal === 'string') {
            cleanVal = cleanVal.replace(/[^0-9.]/g, '');
            const parts = cleanVal.split('.');
            if (parts.length > 2) {
                cleanVal = parts[0] + '.' + parts.slice(1).join('');
            }
        }

        setMeters((prev) => ({
            ...prev,
            [meterType]: {
                ...prev[meterType],
                [field]: cleanVal,
            },
        }));

        // Clear error when field is updated
        if (errors[meterType][field]) {
            setErrors((prev) => ({
                ...prev,
                [meterType]: {
                    ...prev[meterType],
                    [field]: undefined,
                },
            }));
        }
    };

    const validateFields = (): boolean => {
        let isValid = true;
        const newErrors: ErrorsMap = {
            electric: {},
            gas: {},
            water: {},
            oil: {},
        };

        const meterKeys: (keyof MetersMap)[] = ['electric', 'gas', 'water', 'oil'];

        meterKeys.forEach((key) => {
            const meter = meters[key];
            if (!meter.date) {
                newErrors[key].date = 'Date of reading is required';
                isValid = false;
            }
            if (!meter.value || meter.value.trim() === '') {
                newErrors[key].value = 'Value is required';
                isValid = false;
            } else if (isNaN(Number(meter.value)) || !/^\d+(\.\d+)?$/.test(meter.value)) {
                newErrors[key].value = 'Value must be a valid number';
                isValid = false;
            }
            if (!meter.supplier || meter.supplier.trim() === '') {
                newErrors[key].supplier = 'Supplier is required';
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleSave = () => {
        if (!validateFields()) {
            return;
        }

        const payload = {
            readings: [
                {
                    utility_type: 'electric',
                    reading_date: meters.electric.date ? moment(meters.electric.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                    reading_value: meters.electric.value || '',
                    supplier: meters.electric.supplier || '',
                },
                {
                    utility_type: 'gas',
                    reading_date: meters.gas.date ? moment(meters.gas.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                    reading_value: meters.gas.value || '',
                    supplier: meters.gas.supplier || '',
                },
                {
                    utility_type: 'water',
                    reading_date: meters.water.date ? moment(meters.water.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                    reading_value: meters.water.value || '',
                    supplier: meters.water.supplier || '',
                },
                {
                    utility_type: 'oil',
                    reading_date: meters.oil.date ? moment(meters.oil.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                    reading_value: meters.oil.value || '',
                    supplier: meters.oil.supplier || '',
                },
            ],
        };

        if (onSaveMeterReadings && typeof onSaveMeterReadings === 'function') {
            onSaveMeterReadings(payload, () => {
                setIsEditing(false);
            });
        } else {
            setIsEditing(false);
        }
    };

    const formatMeterDate = (date: Date | null) => {
        if (!date) return '-';
        return moment(date).format('DD/MM/YYYY');
    };

    const openDocUrl = (url?: string) => {
        if (!url) return;
        if (Platform.OS === 'web') {
            window.open(url, '_blank', 'noreferrer');
        } else {
            Linking.openURL(url);
        }
    };

    const getFieldError = (type: keyof MetersMap, field: keyof MeterState): string | undefined => {
        return errors[type][field] || customErrors?.[type]?.[field];
    };

    const resolvedData = data?.data || data;
    const checkoutObj = resolvedData?.open_flow_move_out_checkout;
    const openFlow = resolvedData?.open_flow || resolvedData;
    const logoUri = openFlow?.branch?.logo || openFlow?.branch?.company?.logo;
    const agentName = [openFlow?.branch?.company?.name, openFlow?.branch?.name].filter(Boolean).join(", ");
    const inventoryDocs = resolvedData?.open_flow_move_out_inventory_documents || [];
    const hasMeterReadings = Array.isArray(resolvedData?.open_flow_move_out_meter_readings) && resolvedData.open_flow_move_out_meter_readings.length > 0;
    const hasInventoryDocs = inventoryDocs.length > 0;
    const isCheckoutCompleted = checkoutObj ? (checkoutObj.is_completed || (hasMeterReadings && hasInventoryDocs)) : false;

    return (
        <View style={[styles(themeObj).containerStyle, restStyle]}>
            {isEditing ? (
                /* EDIT MODE (IMAGE 2) */
                <View style={{ width: '100%' }}>
                    <ODSText textAlign="flex-start" color="black" type="h4" marginBottom={24}>
                        Meter Readings
                    </ODSText>

                    {/* Electric */}
                    <View style={styles(themeObj).meterEditSection}>
                        <View style={styles(themeObj).meterTitleRow}>
                            <ODSIcon icon="elec" size={20} color={themeObj.main.purple[500]} />
                            <ODSText type="h5" color="black" marginLeft={10}>
                                Electric
                            </ODSText>
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSDatepicker
                                label="Date of Reading"
                                value={meters.electric.date}
                                hints={
                                    getFieldError('electric', 'date')
                                        ? [{ hint: getFieldError('electric', 'date')!, state: 'error' }]
                                        : undefined
                                }
                                onUpdateInput={(d: any) => updateMeterField('electric', 'date', d)}
                            />
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSInput
                                label="Value"
                                value={meters.electric.value}
                                error={!!getFieldError('electric', 'value')}
                                hints={
                                    getFieldError('electric', 'value')
                                        ? [{ hint: getFieldError('electric', 'value')!, state: 'error' }]
                                        : undefined
                                }
                                onChange={(e: any) => updateMeterField('electric', 'value', e?.target?.value ?? e)}
                                onChangeText={(t: string) => updateMeterField('electric', 'value', t)}
                            />
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSInput
                                label="Supplier"
                                value={meters.electric.supplier}
                                error={!!getFieldError('electric', 'supplier')}
                                hints={
                                    getFieldError('electric', 'supplier')
                                        ? [{ hint: getFieldError('electric', 'supplier')!, state: 'error' }]
                                        : undefined
                                }
                                onChange={(e: any) => updateMeterField('electric', 'supplier', e?.target?.value ?? e)}
                                onChangeText={(t: string) => updateMeterField('electric', 'supplier', t)}
                            />
                        </View>
                    </View>

                    {/* Gas */}
                    <View style={styles(themeObj).meterEditSection}>
                        <View style={styles(themeObj).meterTitleRow}>
                            <ODSIcon icon="gas" size={20} color={themeObj.main.purple[500]} />
                            <ODSText type="h5" color="black" marginLeft={10}>
                                Gas
                            </ODSText>
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSDatepicker
                                label="Date of Reading"
                                value={meters.gas.date}
                                hints={
                                    getFieldError('gas', 'date')
                                        ? [{ hint: getFieldError('gas', 'date')!, state: 'error' }]
                                        : undefined
                                }
                                onUpdateInput={(d: any) => updateMeterField('gas', 'date', d)}
                            />
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSInput
                                label="Value"
                                value={meters.gas.value}
                                error={!!getFieldError('gas', 'value')}
                                hints={
                                    getFieldError('gas', 'value')
                                        ? [{ hint: getFieldError('gas', 'value')!, state: 'error' }]
                                        : undefined
                                }
                                onChange={(e: any) => updateMeterField('gas', 'value', e?.target?.value ?? e)}
                                onChangeText={(t: string) => updateMeterField('gas', 'value', t)}
                            />
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSInput
                                label="Supplier"
                                value={meters.gas.supplier}
                                error={!!getFieldError('gas', 'supplier')}
                                hints={
                                    getFieldError('gas', 'supplier')
                                        ? [{ hint: getFieldError('gas', 'supplier')!, state: 'error' }]
                                        : undefined
                                }
                                onChange={(e: any) => updateMeterField('gas', 'supplier', e?.target?.value ?? e)}
                                onChangeText={(t: string) => updateMeterField('gas', 'supplier', t)}
                            />
                        </View>
                    </View>

                    {/* Water */}
                    <View style={styles(themeObj).meterEditSection}>
                        <View style={styles(themeObj).meterTitleRow}>
                            <ODSIcon icon="water" size={20} color={themeObj.main.purple[500]} />
                            <ODSText type="h5" color="black" marginLeft={10}>
                                Water
                            </ODSText>
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSDatepicker
                                label="Date of Reading"
                                value={meters.water.date}
                                hints={
                                    getFieldError('water', 'date')
                                        ? [{ hint: getFieldError('water', 'date')!, state: 'error' }]
                                        : undefined
                                }
                                onUpdateInput={(d: any) => updateMeterField('water', 'date', d)}
                            />
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSInput
                                label="Value"
                                value={meters.water.value}
                                error={!!getFieldError('water', 'value')}
                                hints={
                                    getFieldError('water', 'value')
                                        ? [{ hint: getFieldError('water', 'value')!, state: 'error' }]
                                        : undefined
                                }
                                onChange={(e: any) => updateMeterField('water', 'value', e?.target?.value ?? e)}
                                onChangeText={(t: string) => updateMeterField('water', 'value', t)}
                            />
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSInput
                                label="Supplier"
                                value={meters.water.supplier}
                                error={!!getFieldError('water', 'supplier')}
                                hints={
                                    getFieldError('water', 'supplier')
                                        ? [{ hint: getFieldError('water', 'supplier')!, state: 'error' }]
                                        : undefined
                                }
                                onChange={(e: any) => updateMeterField('water', 'supplier', e?.target?.value ?? e)}
                                onChangeText={(t: string) => updateMeterField('water', 'supplier', t)}
                            />
                        </View>
                    </View>

                    {/* Oil */}
                    <View style={styles(themeObj).meterEditSection}>
                        <View style={styles(themeObj).meterTitleRow}>
                            <ODSIcon icon="oil" size={20} color={themeObj.main.purple[500]} />
                            <ODSText type="h5" color="black" marginLeft={10}>
                                Oil
                            </ODSText>
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSDatepicker
                                label="Date of Reading"
                                value={meters.oil.date}
                                hints={
                                    getFieldError('oil', 'date')
                                        ? [{ hint: getFieldError('oil', 'date')!, state: 'error' }]
                                        : undefined
                                }
                                onUpdateInput={(d: any) => updateMeterField('oil', 'date', d)}
                            />
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSInput
                                label="Value"
                                value={meters.oil.value}
                                error={!!getFieldError('oil', 'value')}
                                hints={
                                    getFieldError('oil', 'value')
                                        ? [{ hint: getFieldError('oil', 'value')!, state: 'error' }]
                                        : undefined
                                }
                                onChange={(e: any) => updateMeterField('oil', 'value', e?.target?.value ?? e)}
                                onChangeText={(t: string) => updateMeterField('oil', 'value', t)}
                            />
                        </View>
                        <View style={styles(themeObj).inputGroup}>
                            <ODSInput
                                label="Supplier"
                                value={meters.oil.supplier}
                                error={!!getFieldError('oil', 'supplier')}
                                hints={
                                    getFieldError('oil', 'supplier')
                                        ? [{ hint: getFieldError('oil', 'supplier')!, state: 'error' }]
                                        : undefined
                                }
                                onChange={(e: any) => updateMeterField('oil', 'supplier', e?.target?.value ?? e)}
                                onChangeText={(t: string) => updateMeterField('oil', 'supplier', t)}
                            />
                        </View>
                    </View>

                    {/* Save Button */}
                    <View style={{ marginTop: 30, marginBottom: 10 }}>
                        <ODSButton
                            restStyle={{ width: '100%', height: 48, borderRadius: 24 }}
                            type="primary"
                            themeColor={themeObj.main.blue}
                            onPress={handleSave}
                        >
                            Save and send to agent
                        </ODSButton>
                    </View>
                </View>
            ) : (
                /* VIEW MODE (IMAGE 1) */
                <View style={{ width: '100%' }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                        <ODSText textAlign="flex-start" color="black" type="h6">
                            Check out
                        </ODSText>
                        <TouchableOpacity
                            onPress={() => setIsEditing(true)}
                            style={styles(themeObj).editButton}
                        >
                            <ODSIcon icon="TB-Edit" size={18} color={themeObj.button.main} />
                            <ODSText marginLeft={6} type="sm-bold" color={themeObj.button.main}>
                                Edit
                            </ODSText>
                        </TouchableOpacity>
                    </View>

                    <View style={{ flexDirection: 'row', justifyContent: 'flex-start', marginBottom: 16 }}>
                        <View style={{ flexDirection: 'column', flex: 1 }}>
                            <ODSText textAlign="flex-start" color="black" type="button">
                                Date sent
                            </ODSText>
                            <ODSText textAlign="flex-start" marginTop={6} color={themeObj.text.secondary} type="sm">
                                {checkoutObj?.tenants_notified_at ? moment(checkoutObj.tenants_notified_at).format('DD/MM/YYYY') : 'Not sent yet'}
                            </ODSText>
                        </View>

                        <View style={{ flexDirection: 'column', flex: 1 }}>
                            <ODSText textAlign="flex-start" color="black" type="button">
                                Agent:
                            </ODSText>
                            {agentName ? (
                                <ODSText textAlign="flex-start" marginTop={4} color={themeObj.text.secondary} type="sm">
                                    {agentName}
                                </ODSText>
                            ) : null}
                            {logoUri ? (
                                <Image
                                    source={{ uri: logoUri }}
                                    style={{ width: 100, height: 24, resizeMode: 'contain', marginTop: 6 }}
                                />
                            ) : (
                                <Image
                                    source={require('../../../images/obxLogo.png')}
                                    style={{ width: 100, height: 24, resizeMode: 'contain', marginTop: 6 }}
                                />
                            )}
                        </View>
                    </View>

                    <View style={{ marginBottom: 20, flexDirection: 'row', flexWrap: 'wrap' }}>
                        <View style={{ flexDirection: 'column', flex: 1 }}>
                            <ODSText textAlign="flex-start" color="black" type="button">
                                Status
                            </ODSText>
                            <ODSText textAlign="flex-start" marginTop={6} color={themeObj.text.secondary} type="sm">
                                {isCheckoutCompleted ? 'Completed' : 'In Progress'}
                            </ODSText>
                        </View>
                        {checkoutObj?.checkout_date && (
                            <View style={{ flexDirection: 'column', flex: 1 }}>
                                <ODSText textAlign="flex-start" color="black" type="button">
                                    Checkout Date & Time
                                </ODSText>
                                <ODSText textAlign="flex-start" marginTop={6} color={themeObj.text.secondary} type="sm">
                                    {moment(checkoutObj.checkout_date).format('DD/MM/YYYY')} {checkoutObj.checkout_time ? `@ ${checkoutObj.checkout_time}` : ''}
                                </ODSText>
                            </View>
                        )}
                    </View>
                    <View style={{ height: 1, backgroundColor: '#E5E7EB', width: '100%', marginBottom: 24 }} />

                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                        <ODSText textAlign="flex-start" color="black" type='h6'>
                            Meter Readings
                        </ODSText>
                    </View>

                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                        {/* Electric */}
                        <View style={{ flexDirection: 'row', alignItems: 'center', width: layout === 'phone' ? '100%' : '48%', marginBottom: 16 }}>
                            <ODSIcon icon="elec" size={24} color={themeObj.main.purple[500]} />
                            <View style={{ flexDirection: 'column', flex: 1, marginLeft: 12 }}>
                                <ODSText textAlign="left" color="black" type="button">
                                    Electric
                                </ODSText>
                                <ODSText textAlign="left" marginTop={4} color={themeObj.text.secondary} type="sm">
                                    {meters.electric.value ? `${meters.electric.value} - ${formatMeterDate(meters.electric.date)}` : 'No reading yet'}
                                </ODSText>
                            </View>
                        </View>

                        {/* Gas */}
                        <View style={{ flexDirection: 'row', alignItems: 'center', width: layout === 'phone' ? '100%' : '48%', marginBottom: 16 }}>
                            <ODSIcon icon="gas" size={24} color={themeObj.main.purple[500]} />
                            <View style={{ flexDirection: 'column', flex: 1, marginLeft: 12 }}>
                                <ODSText textAlign="left" color="black" type="button">
                                    Gas
                                </ODSText>
                                <ODSText textAlign="left" marginTop={4} color={themeObj.text.secondary} type="sm">
                                    {meters.gas.value ? `${meters.gas.value} - ${formatMeterDate(meters.gas.date)}` : 'No reading yet'}
                                </ODSText>
                            </View>
                        </View>

                        {/* Water */}
                        <View style={{ flexDirection: 'row', alignItems: 'center', width: layout === 'phone' ? '100%' : '48%', marginBottom: 16 }}>
                            <ODSIcon icon="water" size={24} color={themeObj.main.purple[500]} />
                            <View style={{ flexDirection: 'column', flex: 1, marginLeft: 12 }}>
                                <ODSText textAlign="left" color="black" type="button">
                                    Water
                                </ODSText>
                                <ODSText textAlign="left" marginTop={4} color={themeObj.text.secondary} type="sm">
                                    {meters.water.value ? `${meters.water.value} - ${formatMeterDate(meters.water.date)}` : 'No reading yet'}
                                </ODSText>
                            </View>
                        </View>

                        {/* Oil */}
                        <View style={{ flexDirection: 'row', alignItems: 'center', width: layout === 'phone' ? '100%' : '48%', marginBottom: 16 }}>
                            <ODSIcon icon="oil" size={24} color={themeObj.main.purple[500]} />
                            <View style={{ flexDirection: 'column', flex: 1, marginLeft: 12 }}>
                                <ODSText textAlign="left" color="black" type="button">
                                    Oil
                                </ODSText>
                                <ODSText textAlign="left" marginTop={4} color={themeObj.text.secondary} type="sm">
                                    {meters.oil.value ? `${meters.oil.value} - ${formatMeterDate(meters.oil.date)}` : 'No reading yet'}
                                </ODSText>
                            </View>
                        </View>
                    </View>
                    <View style={{ height: 1, backgroundColor: '#E5E7EB', width: '100%', marginTop: 24 }} />

                    {inventoryDocs && inventoryDocs.length > 0 && (
                        <View style={{ marginTop: 8, marginBottom: 16 }}>
                            <ODSText textAlign="flex-start" color="black" type="h6" marginBottom={16}>
                                Inventory Documents
                            </ODSText>
                            {inventoryDocs.map((doc: any, index: number) => (
                                <TouchableOpacity
                                    key={doc?.open_flow_move_out_inventory_document_id || index}
                                    onPress={() => openDocUrl(doc?.file_url)}
                                    style={styles(themeObj).docCard}
                                >
                                    <View style={styles(themeObj).downloadIconBtn}>
                                        <ODSIcon icon="TB-Download" size={24} color={themeObj.button.main} />
                                    </View>

                                    <View style={styles(themeObj).docTextContainer}>
                                        <ODSText type="md-bold" color={themeObj.text.primary} textAlign="left">
                                            {doc?.file_name || 'Inventory Document'}
                                        </ODSText>
                                        <ODSText type="sm" color={themeObj.text.secondary} marginTop={4} textAlign="left">
                                            {moment(doc?.createdAt).format("DD/MM/YYYY")}
                                        </ODSText>
                                    </View>

                                    <View style={styles(themeObj).statusContainer}>
                                        <ODSIcon icon="TBDoc" size={20} color="#1FA6B2" />
                                        <ODSText type="xs" color="#1FA6B2" marginTop={2}>
                                            View
                                        </ODSText>
                                    </View>
                                </TouchableOpacity>
                            ))}
                        </View>
                    )}
                </View>
            )}
        </View>
    );
};

const styles = (theme: any) =>
    StyleSheet.create({
        containerStyle: {
            width: getScreenType() == 'web' ? 680 : '100%',
            borderRadius: 12,
            shadowColor: '#101828',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.1,
            shadowRadius: 2,
            elevation: 2,
            backgroundColor: theme.neutral['white'],
            paddingVertical: 32,
            paddingHorizontal: 28,
            borderWidth: 1,
            borderColor: '#D0D6DD',
            alignSelf: 'center',
            marginBottom: 100,
        },
        editButton: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: 6,
            paddingHorizontal: 12,
            borderRadius: 20,
            borderWidth: 1,
            borderColor: theme.button.main,
        },
        meterEditSection: {
            marginBottom: 16,
        },
        meterTitleRow: {
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: 16,
        },
        inputGroup: {
            marginBottom: 12,
        },
        sectionDivider: {
            height: 1,
            backgroundColor: '#E5E7EB',
            width: '100%',
            marginVertical: 16,
        },
        docCard: {
            flexDirection: 'row',
            alignItems: 'center',
            borderWidth: 1,
            borderColor: '#E5E7EB',
            borderRadius: 12,
            paddingHorizontal: 20,
            paddingVertical: 16,
            backgroundColor: '#FFFFFF',
            marginBottom: 16,
        },
        downloadIconBtn: {
            marginRight: 4,
        },
        docTextContainer: {
            flex: 1,
            marginLeft: 12,
        },
        statusContainer: {
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 12,
        },
    });

export default CheckOutBox;
